import React, { useEffect, useState } from "react";
import { Button, EmptyModal, Modal, Select } from "../../../../components/core";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  BRANCHES,
  PARTNERS,
  UPDATE_AGENT,
} from "../../../../constants/graphql/partner-agent-queries";

const operatorId = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user")).operator_id
  : "";

export default function EditPartnerBranchesModal({
  isOpen,
  onClose,
  partnerAgentId,
  agentData,
  onSuccess,
}) {
  const initial = {
    partnerId: agentData?.partnerId || null,
    partnerBranchId: agentData?.partnerBranchId || null,
  };

  const [selectedData, setSelectedData] = useState(initial);
  const [validation, setValidation] = useState({ open: false });

  const changesMade =
    selectedData.partnerId !== initial.partnerId ||
    selectedData.partnerBranchId !== initial.partnerBranchId;

  const showError = (message) =>
    setValidation({
      open: true,
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: "Error",
      message,
      actions: (
        <Button
          value="OK"
          className="p-2 w-full"
          onClick={() => setValidation({ open: false })}
        />
      ),
    });

  const showSuccess = (message, onOk) =>
    setValidation({
      open: true,
      icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
      title: "Success",
      message,
      actions: (
        <Button
          value="OK"
          className="p-2 w-full"
          onClick={() => {
            setValidation({ open: false });
            onOk?.();
          }}
        />
      ),
    });

  const { data: partnersData } = useQuery(PARTNERS, {
    variables: { operatorId },
    skip: !isOpen,
  });

  const [fetchBranches, { data: branchesData, loading: fetchingBranches }] =
    useLazyQuery(BRANCHES);

  useEffect(() => {
    if (selectedData.partnerId) {
      fetchBranches({
        variables: { operatorId, partnerId: selectedData.partnerId },
      });
    }
  }, [selectedData.partnerId]);

  // Sync initial values when agentData changes (e.g. modal reopened)
  useEffect(() => {
    if (isOpen) {
      setSelectedData({
        partnerId: agentData?.partnerId || null,
        partnerBranchId: agentData?.partnerBranchId || null,
      });
    }
  }, [isOpen]);

  const partnerOptions = (partnersData?.partners || []).map((p) => ({
    value: p.partnerId,
    name: p.companyNickname,
  }));

  const branchOptions = (branchesData?.partnerBranches || []).map((b) => ({
    value: b.partnerBranchId,
    name: b.partnerBranch,
  }));

  const [updateAgent, { loading: saving }] = useMutation(UPDATE_AGENT);

  const handleChange = (e, field) => {
    const value = e.target.value;
    if (field === "partnerId") {
      setSelectedData((prev) => ({
        ...prev,
        partnerId: value,
        partnerBranchId: null,
      }));
    } else {
      setSelectedData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleClose = () => {
    setSelectedData(initial);
    onClose();
  };

  const handleSave = () => {
    if (!selectedData.partnerId) {
      showError("Please select a partner.");
      return;
    }
    if (!selectedData.partnerBranchId) {
      showError("Please select a partner branch.");
      return;
    }

    updateAgent({
      variables: {
        operatorId,
        partnerAgentId: parseInt(partnerAgentId),
        partnerId: parseInt(selectedData.partnerId),
        partnerBranchId: parseInt(selectedData.partnerBranchId),
      },
    })
      .then((res) => {
        if (res.data) {
          showSuccess("Agent updated successfully.", () => {
            onSuccess?.();
            handleClose();
          });
        }
      })
      .catch((err) => {
        showError(err.message || "An error occurred while updating the agent.");
      });
  };

  return (
    <>
      <EmptyModal open={isOpen} height="auto" width="420px">
        <h2 className="text-base font-semibold mb-4">
          Edit Courier Assignment
        </h2>
        <div className="grid grid-cols-4 gap-2 w-full">
          <div className="col-span-2">
            <p className="text-base mt-2 flex">
              <b className="text-red-700 mr-2">*</b>Partner:
            </p>
          </div>
          <div className="bg-slate-50 col-span-2">
            <Select
              items={partnerOptions}
              defaultOption={{ value: null, name: "-- Select Partner --" }}
              value={selectedData.partnerId}
              onChange={(e) => handleChange(e, "partnerId")}
              name="partnerId"
            />
          </div>

          <div className="col-span-2">
            <p className="text-base mt-2 flex">
              <b className="text-red-700 mr-2">*</b>Branch:
            </p>
          </div>
          <div className="bg-slate-50 col-span-2">
            <Select
              items={branchOptions}
              defaultOption={{
                value: null,
                name: fetchingBranches ? "Loading..." : "-- Select Branch --",
              }}
              value={selectedData.partnerBranchId}
              onChange={(e) => handleChange(e, "partnerBranchId")}
              name="partnerBranchId"
              disable={!selectedData.partnerId || fetchingBranches}
            />
          </div>
        </div>
        <hr className="my-6" />
        <div className="grid grid-cols-2 gap-4">
          <Button className="p-2" value="Cancel" onClick={handleClose} />
          <Button
            className="p-2"
            value={saving ? "Saving..." : "Save Changes"}
            onClick={handleSave}
            disabled={!changesMade || saving}
          />
        </div>
      </EmptyModal>

      <Modal
        open={validation.open}
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        actions={validation.actions}
      />
    </>
  );
}
